import React, { useState, useEffect } from "react";
import { Typography, Select, MenuItem, Grid, Box, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from "@mui/material";
import StandardDataGrid from "../../components/tables/standard_table.js";
import LineChartBasic from "../../components/charts/LineChartBasic.js";
import { getVesselReportViews, getVesselReportMarketSumView, getVesselReportPortSumView, getVesselReportHPEView, getVesselReportHPEDayView } from "../../api/api.js";
import countryList from 'react-select-country-list';
import DirectionsBoatFilledOutlinedIcon from '@mui/icons-material/DirectionsBoatFilledOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import AnchorOutlinedIcon from '@mui/icons-material/AnchorOutlined';
import PieChartBasic from "../../components/charts/PieChartBasic.js";
import { BarChart } from '@mui/x-charts/BarChart';
import { useTheme } from "@mui/material";
import { dataVisColors } from "@mercedes-benz/mui5-theme";
import { useRoleCheckDashboard } from "../../auth/authUtils.js";
import { renderStatus } from "../../components/tables/cellRenderes/statusDelaySales.js";

const ShippingReportPage = () => {
    useRoleCheckDashboard("ShippingReport");
    // State hooks to manage data and selections
    const [vesselReports, setVesselReports] = useState([]);
    const [selectedDeparturePort, setSelectedDeparturePort] = useState("");
    const [hpeReports, setHpeReports] = useState([]);
    const [marketSum, setMarketSum] = useState([]);
    const [selectedPort, setSelectedPort] = useState("");
    const [selectedPortName, setSelectedPortName] = useState("");
    const [tableData, setTableData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [filterValue, setFilterValue] = useState("all");
    const [selectedDestination, setSelectedDestination] = useState('');
    const [chartData, setChartData] = useState({ xAxis: [], seriesData: [] });
    const [countryCode, setCountryCode] = useState("");
    const [lineChartMarketVehicleSum, setLineChartMarketVehicleSum] = useState([]);
    const [lineChartMarketShipSum, setLineChartMarketShipSum] = useState([]);
    const [tableDataMarketSum, setTableDataMarketSum] = useState([]);
    const [portSumViewResult, setPortSumViewResult] = useState([]);
    const [lineChartMarketVehicleSumFiltered, setLineChartMarketVehicleSumFiltered] = useState('');
    const [lineChartMarketShipSumFiltered, setLineChartMarketShipSumFiltered] = useState('');
    const [tableDataMarketSumFiltered, setTableDataMarketSumFiltered] = useState('');
    const [portSumViewResultFiltered, setPortSumViewResultFiltered] = useState([]);
    const [pieChartData, setPieChartData] = useState([]);
    const [portMenuItems, setPortMenuItems] = useState([]);
    const theme = useTheme();
    const [colorScheme, setColorScheme] = useState([]);
    const [timeRange, setTimeRange] = useState('data available');

    useEffect(() => {
        const themeType = theme.palette.mode === 'light' ? 'light' : 'dark';
        setColorScheme(dataVisColors.basic[themeType]);
    }, [theme.palette.mode]);

    // Column definitions for the data grid
    const columns = [
        { field: "id", headerName: "ID" },
        { field: "vessel", headerName: "Vessel", width: 150 },
        { field: "initialETD", headerName: "Initial ETD", width: 130 },
        { field: "latestETD", headerName: "Latest ETD", width: 130 },
        { field: "departureDelay", headerName: "Departure Delay", width: 150 },
        { field: "status", headerName: "Status", width: 150, renderCell: renderStatus },
        { field: "plannedVolume", headerName: "Planned Volume", width: 150 },
        { field: "adjustedVolume", headerName: "Adjusted Volume", width: 150 },
        { field: "stock", headerName: "Stock", width: 120 },
        { field: "shippingCapacityBottleneck", headerName: "Shipping Capacity Bottleneck", width: 250 },
        { field: "actualVolumeShipped", headerName: "Actual Volume Shipped", width: 200 },
        { field: "initialETA", headerName: "Initial ETA", width: 130 },
        { field: "actualETA", headerName: "Actual ETA", width: 130 },
        { field: "arrivalDelay", headerName: "Arrival Delay", width: 130 },
        { field: "arrivalActuals", headerName: "Actual Arrivals", width: 150 },
    ];

    // Fetch initial vessel reports on component mount
    useEffect(() => {
        getVesselReportViews().then((data) => setVesselReports(data));
    }, []);

    useEffect(() => {
        setPortMenuItems(getUniquePortMenuItems(portSumViewResult));
        filterDataByTime(filterValue);
        console.log(lineChartMarketShipSum);
        console.log(lineChartMarketVehicleSum);
        console.log(tableDataMarketSum);
        console.log(portSumViewResult);
    }, [portSumViewResult, lineChartMarketShipSum, lineChartMarketVehicleSum, tableDataMarketSum]);

    // Handle departure port change
    const handleDeparturePortChange = (event) => {
        setSelectedDeparturePort(event.target.value);
        if (event.target.value) {
            getVesselReportMarketSumView(event.target.value).then((data) => setMarketSum(data));
        }
        setSelectedDestination("");
        setSelectedPort(""); // Reset the port selection
        setSelectedPortName(""); // Reset the port name
        setTableData([]); // Reset the table data
    };

    const handleDestinationChange = (event) => {
        setSelectedDestination(event.target.value);
        if (event.target.value) {
            setLineChartMarketVehicleSum(filterByCountryCode(event.target.value));
            setLineChartMarketShipSum(filterByCountryCodeShips(event.target.value));
            setTableDataMarketSum(generateTable(filterByCountryCode(event.target.value), filterByCountryCodeShips(event.target.value)));
            getVesselReportPortSumView(selectedDeparturePort, event.target.value).then((data) => setPortSumViewResult(data));
        }
        setSelectedPort(""); // Reset the port selection
        setSelectedPortName(""); // Reset the port name
        setTableData([]); // Reset the table data
    };

    const handlePortChange = (event) => {
        const selectedId = event.target.value;
        const selectedName = event.target.value; // Get the selected port name
        setSelectedPort(selectedId);
        setSelectedPortName(selectedName);

        if (selectedName) {
            // Fetch HPE day reports based on selected port
            getVesselReportHPEDayView(selectedId).then((data) => {
                const formattedData = data.map((report, index) => {
                    const departureDelay = calculateDaysDifference(report.day, report.initialEtd);
                    let status = 'OnTime';
                    if (departureDelay > 7) {
                        status = 'MajorDelay';
                    } else if (departureDelay >= 3) {
                        status = 'MinorDelay';
                    }

                    return {
                        id: index + 1,
                        vessel: report.shipName,
                        initialETD: report.initialEtd,
                        latestETD: report.day,
                        departureDelay: departureDelay,
                        plannedVolume: report.volumeFirstOffer,
                        adjustedVolume: report.volumePoAdjusted,
                        stock: report.volumeReadyAtClosing,
                        shippingCapacityBottleneck: report.bottleneckCapaAbs,
                        actualVolumeShipped: report.volumeShipEffective,
                        initialETA: report.eta,
                        actualETA: report.eta,
                        arrivalDelay: calculateDaysDifference(report.eta, report.eta),
                        arrivalActuals: report.volumeShipEffective,
                        status: status
                    };
                });
                setTableData(formattedData);
                applyFilter(formattedData, filterValue);
            });
        }
    };

    const filterDataByTime = (filter) => {
        console.log("im here");
        if (portSumViewResult.length && lineChartMarketShipSum && lineChartMarketVehicleSum && tableDataMarketSum) {

            const now = new Date();

            // Helper function to parse dates from xAxis (for line chart data)
            const parseDate = (dateStr) => {
                const [month, year] = dateStr.split(' ');
                return new Date(`${month} 1, ${year}`);  // Use day 1 for simplicity
            };

            // Helper function to parse dates from vesselReports
            const parseVesselDate = (year, month) => {
                return new Date(year, month - 1, 1);  // month is 0-indexed in Date
            };

            // Function to filter xAxis and seriesData for chart data
            const filterChartData = (data, dateLimit) => {
                const filteredData = [];

                data.xAxis.forEach((dateStr, index) => {
                    const date = parseDate(dateStr);
                    if (date >= dateLimit) {
                        filteredData.push({
                            label: dateStr,
                            value: data.seriesData[index]
                        });
                    }
                });

                return filteredData;
            };

            // Function to filter vesselReports data
            const filterVesselReports = (data, dateLimit) => {
                return data.filter(report => {
                    const reportDate = parseVesselDate(report.year, report.month);
                    return reportDate >= dateLimit;
                });
            };

            // Function to filter tableDataMarketSum data
            const filterTableData = (data, dateLimit) => {
                const quarterlyColumns = ['Q12024', 'Q22024', 'Q32024', 'Q42024'];
                const filteredColumns = data.columns.filter(column => {
                    if (column.field === 'id') return true; // Always include the ID column
                    if (quarterlyColumns.includes(column.field)) return true; // Always include quarterly columns
                    const [month, year] = column.field.match(/([A-Za-z]+)(\d{4})/).slice(1, 3);
                    const columnDate = new Date(`${month} 1, ${year}`);
                    return columnDate >= dateLimit;
                });

                const filteredRows = data.rows.map(row => {
                    const filteredRow = { id: row.id };
                    filteredColumns.forEach(column => {
                        filteredRow[column.field] = row[column.field];
                    });
                    return filteredRow;
                });

                return { columns: filteredColumns, rows: filteredRows };
            };

            let filteredShipData = [];
            let filteredVehicleData = [];
            let filteredPorts = [];
            let filteredTableData = {};

            switch (filter) {
                case "6months":
                    const sixMonthsAgo = new Date(now.setMonth(now.getMonth() - 6));
                    filteredShipData = filterChartData(lineChartMarketShipSum, sixMonthsAgo);
                    filteredVehicleData = filterChartData(lineChartMarketVehicleSum, sixMonthsAgo);
                    filteredPorts = filterVesselReports(portSumViewResult, sixMonthsAgo);
                    filteredTableData = filterTableData(tableDataMarketSum, sixMonthsAgo);
                    break;

                case "1year":
                    const oneYearAgo = new Date(now.setFullYear(now.getFullYear() - 1));
                    filteredShipData = filterChartData(lineChartMarketShipSum, oneYearAgo);
                    filteredVehicleData = filterChartData(lineChartMarketVehicleSum, oneYearAgo);
                    filteredPorts = filterVesselReports(portSumViewResult, oneYearAgo);
                    filteredTableData = filterTableData(tableDataMarketSum, oneYearAgo);
                    break;

                case "currentYear":
                    const startOfYear = new Date(now.getFullYear(), 0, 1);
                    filteredShipData = filterChartData(lineChartMarketShipSum, startOfYear);
                    filteredVehicleData = filterChartData(lineChartMarketVehicleSum, startOfYear);
                    filteredPorts = filterVesselReports(portSumViewResult, startOfYear);
                    filteredTableData = filterTableData(tableDataMarketSum, startOfYear);
                    break;

                case "currentQuarter":
                    const currentQuarter = Math.floor((now.getMonth() + 3) / 3);
                    const startOfQuarter = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
                    filteredShipData = filterChartData(lineChartMarketShipSum, startOfQuarter);
                    filteredVehicleData = filterChartData(lineChartMarketVehicleSum, startOfQuarter);
                    filteredPorts = filterVesselReports(portSumViewResult, startOfQuarter);
                    filteredTableData = filterTableData(tableDataMarketSum, startOfQuarter);
                    break;

                case "all":
                default:
                    filteredShipData = lineChartMarketShipSum.xAxis.map((label, index) => ({
                        label,
                        value: lineChartMarketShipSum.seriesData[index]
                    }));

                    filteredVehicleData = lineChartMarketVehicleSum.xAxis.map((label, index) => ({
                        label,
                        value: lineChartMarketVehicleSum.seriesData[index]
                    }));

                    filteredPorts = portSumViewResult;
                    filteredTableData = tableDataMarketSum;
                    break;
            }

            // Update state with filtered data
            setLineChartMarketShipSumFiltered(filteredShipData);
            setLineChartMarketVehicleSumFiltered(filteredVehicleData);
            setPortSumViewResultFiltered(filteredPorts);
            setPieChartData(transformToPieChartData(filteredPorts));
            setTableDataMarketSumFiltered(filteredTableData);
            console.log(filteredShipData);
            console.log(filteredVehicleData);
            console.log(filteredPorts);
            console.log(filteredTableData);
        }
    };

    const getUniquePortMenuItems = (data) => {
        // Use a Map to store unique ports with their matching idVesselReportHpe
        const uniquePorts = new Map();

        data.forEach(item => {
            if (!uniquePorts.has(item.portName)) {
                uniquePorts.set(item.portName, item.idVesselReportHpe);
            }
        });

        // Convert the Map into an array of objects for dropdown rendering
        return Array.from(uniquePorts).map(([portName, idVesselReportHpe]) => ({
            label: portName,
            value: idVesselReportHpe,
        }));
    };

    function filterByCountryCode(countryCode) {
        // Abbreviations for months
        const monthAbbr = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        // Filter, sort, and map the data
        const filteredData = marketSum
            .filter(item => item.countryCode === countryCode && item.volumeShipEffective !== null)
            .sort((a, b) => {
                // First sort by year, then by month
                if (a.year !== b.year) {
                    return a.year - b.year;
                }
                return a.month - b.month;
            })
            .map(item => ({
                xAxis: `${monthAbbr[item.month - 1]} ${item.year}`,
                seriesData: item.volumeShipEffective || 0
            }));

        // Create final xAxis and seriesData arrays
        const result = {
            xAxis: filteredData.map(item => item.xAxis),
            seriesData: filteredData.map(item => item.seriesData)
        };

        return result;
    }

    function filterByCountryCodeShips(countryCode) {
        // Abbreviations for months
        const monthAbbr = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        // Filter, sort, and map the data
        const filteredData = marketSum
            .filter(item => item.countryCode === countryCode && item.shipDepartures !== null)
            .sort((a, b) => {
                // First sort by year, then by month
                if (a.year !== b.year) {
                    return a.year - b.year;
                }
                return a.month - b.month;
            })
            .map(item => ({
                xAxis: `${monthAbbr[item.month - 1]} ${item.year}`,
                seriesData: item.shipDepartures || 0
            }));

        // Create final xAxis and seriesData arrays
        const result = {
            xAxis: filteredData.map(item => item.xAxis),
            seriesData: filteredData.map(item => item.seriesData)
        };

        return result;
    }

    function transformToPieChartData(data) {
        const result = [];

        // Map to aggregate data by portName
        const aggregationMap = new Map();

        data.forEach(item => {
            const { portName, volumeShipEffective } = item;

            // If the port is not yet in the map, initialize it
            if (!aggregationMap.has(portName)) {
                aggregationMap.set(portName, {
                    value: 0,  // Initialize sum for the port
                    label: portName
                });
            }

            // Add the current `volumeShipEffective` to the existing sum
            const aggregatedItem = aggregationMap.get(portName);
            aggregatedItem.value += volumeShipEffective || 0;
        });

        // Convert the map to the desired output format
        let idCounter = 0;
        aggregationMap.forEach((value, portName) => {
            result.push({
                id: idCounter++,  // Incremental ID
                value: value.value,
                label: `${portName}: ${value.value}`
            });
        });

        return result;
    }

    function generateTable(carData, shipData) {
        // Step 1: Extract xAxis (months with year) and ensure same length for both datasets
        const months = shipData.xAxis;
        console.log(months);

        // Step 2: Create the columns dynamically, including the year
        const columns = [{ field: 'id', headerName: 'ID' }];
        const years = new Set(); // Track the years to add quarterly columns later
        const quarterMapping = {
            Q1: ['Jan', 'Feb', 'Mar'],
            Q2: ['Apr', 'May', 'Jun'],
            Q3: ['Jul', 'Aug', 'Sep'],
            Q4: ['Oct', 'Nov', 'Dec']
        };

        months.forEach(monthYear => {
            const [month, year] = monthYear.split(' '); // e.g. ['Jan', '2024']
            columns.push({ field: `${month}${year}`, headerName: `${month} ${year}` });
            years.add(year); // Collect all years

            // Step 3: Check if the month is the last of a quarter and add the quarterly column after it
            for (const [quarter, monthsInQuarter] of Object.entries(quarterMapping)) {
                if (month === monthsInQuarter[2]) { // Check if the month is the last in the quarter
                    columns.push({ field: `${quarter}${year}`, headerName: `${quarter} ${year}` });
                }
            }
        });

        // Step 4: Create rows for ship and car departures, filling data per month
        function createRow(id, xAxis, seriesData) {
            const row = { id };
            let xAxisCounter = 0;
            months.forEach((monthYear, index) => {
                const [month, year] = monthYear.split(' ');
                if (xAxis[xAxisCounter] === monthYear) {
                    row[`${month}${year}`] = seriesData[xAxisCounter] || 0;
                    xAxisCounter++;
                }
                else {
                    row[`${month}${year}`] = 0;
                }
            });

            // Calculate quarterly sums for each year
            years.forEach(year => {
                for (const [quarter, monthsInQuarter] of Object.entries(quarterMapping)) {
                    const quarterSum = monthsInQuarter.reduce((sum, month) => {
                        return sum + (row[`${month}${year}`] || 0);
                    }, 0);
                    row[`${quarter}${year}`] = quarterSum;
                }
            });

            return row;
        }

        const carRow = createRow("Car Departures", carData.xAxis, carData.seriesData);
        const shipRow = createRow("Ship Departures", shipData.xAxis, shipData.seriesData);

        // Step 5: Return the final table structure
        return {
            columns,
            rows: [shipRow, carRow]
        };
    }

    // Handle filter change
    const handleFilterChange = (event) => {
        const value = event.target.value;
        setFilterValue(value);
        applyFilter(tableData, value);
        filterDataByTime(value);
    };

    // Apply filter to data
    const applyFilter = (data, filter) => {
        const now = new Date();
        let filteredData = data;

        switch (filter) {
            case "6months":
                const sixMonthsAgo = new Date(now.setMonth(now.getMonth() - 6));
                filteredData = data.filter(report => new Date(report.latestETD) >= sixMonthsAgo);
                setTimeRange("last 6 months");
                break;
            case "1year":
                const oneYearAgo = new Date(now.setFullYear(now.getFullYear() - 1));
                filteredData = data.filter(report => new Date(report.latestETD) >= oneYearAgo);
                setTimeRange("last 12 months");
                break;
            case "currentYear":
                const startOfYear = new Date(now.getFullYear(), 0, 1);
                filteredData = data.filter(report => new Date(report.latestETD) >= startOfYear);
                setTimeRange("current year");
                break;
            case "currentQuarter":
                const currentQuarter = Math.floor((now.getMonth() + 3) / 3);
                const startOfQuarter = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
                filteredData = data.filter(report => new Date(report.latestETD) >= startOfQuarter);
                setTimeRange("current quarter");
                break;
            case "all":
            default:
                filteredData = data;
                setTimeRange("data available");
                break;
        }
        setFilteredData(filteredData);
        aggregateDataForChart(filteredData); // Aggregate data for chart after filtering
    };

    // Aggregate data for the chart
    const aggregateDataForChart = (data) => {
        const aggregatedData = {};

        // Aggregate the data by month and year
        data.forEach((report) => {
            const date = new Date(report.latestETD);
            const month = date.getMonth();
            const year = date.getFullYear();
            const key = `${year}-${month}`;

            if (!aggregatedData[key]) {
                aggregatedData[key] = 0;
            }
            aggregatedData[key] += report.actualVolumeShipped;
        });

        // Sort the keys in chronological order
        const sortedKeys = Object.keys(aggregatedData).sort((a, b) => {
            const [yearA, monthA] = a.split('-').map(Number);
            const [yearB, monthB] = b.split('-').map(Number);
            if (yearA !== yearB) {
                return yearA - yearB;
            }
            return monthA - monthB;
        });

        // Format the sorted keys to "MMM YYYY"
        const formattedKeys = sortedKeys.map((key) => {
            const [year, month] = key.split('-').map(Number);
            return `${new Date(year, month).toLocaleString('default', { month: 'short' })}. ${year}`;
        });

        const xAxis = formattedKeys;
        const seriesData = sortedKeys.map((key) => aggregatedData[key]);

        setChartData({ xAxis, seriesData });
    };

    // Calculate the difference in days between two dates
    const calculateDaysDifference = (date1, date2) => {
        const diffTime = Math.abs(new Date(date2) - new Date(date1));
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    return (
        <Box sx={{ p: 5 }}>
            <Grid container spacing={2} marginBottom={2}>
                <Grid item xs={12} md={8}>
                    <Typography variant="h2">Shipping Report</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl component="fieldset" margin="normal">
                        <FormLabel component="legend">Filter by Time Range</FormLabel>
                        <RadioGroup row value={filterValue} onChange={handleFilterChange}>
                            {/* <FormControlLabel value="currentQuarter" control={<Radio />} label="Current Quarter" /> */}
                            <FormControlLabel value="currentYear" control={<Radio />} label="Current Year" />
                            <FormControlLabel value="6months" control={<Radio />} label="6 Months" />
                            <FormControlLabel value="1year" control={<Radio />} label="1 Year" />
                            <FormControlLabel value="all" control={<Radio />} label="All" />
                        </RadioGroup>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography>Select Report</Typography>
                    <Select
                        value={selectedDeparturePort}
                        onChange={handleDeparturePortChange}
                        variant="outlined"
                        displayEmpty
                        fullWidth
                    >
                        <MenuItem value="" >
                            <em>None</em>
                        </MenuItem>
                        {vesselReports.map((report) => (
                            <MenuItem key={report.id} value={report.id}>
                                {report.filename}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography>Select Country</Typography>
                    <Select
                        value={selectedDestination}
                        onChange={handleDestinationChange}
                        variant="outlined"
                        displayEmpty
                        fullWidth
                        disabled={!selectedDeparturePort}
                    >
                        <MenuItem value="" >
                            <em>None</em>
                        </MenuItem>
                        {[...new Set(marketSum.map(report => report.countryCode))].map((countryCode, index) => (
                            <MenuItem key={index} value={countryCode}>
                                {countryList().getLabel(countryCode)}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography>Select Port</Typography>
                    <Select
                        value={selectedPort}
                        onChange={handlePortChange}
                        variant="outlined"
                        displayEmpty
                        fullWidth
                        disabled={!selectedDestination}
                    >
                        <MenuItem value="" >
                            <em>None</em>
                        </MenuItem>
                        {portMenuItems.map((report) => (
                            <MenuItem key={report.value} value={report.value}>
                                {report.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            </Grid>
            {selectedDestination && !selectedPort && lineChartMarketShipSumFiltered && lineChartMarketVehicleSumFiltered && (
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant='h3' marginTop={5} marginBottom={5}>
                                <DirectionsCarFilledOutlinedIcon /> Total numbers of Cars shipped to {selectedDestination}
                            </Typography>
                            <BarChart
                                dataset={lineChartMarketVehicleSumFiltered}
                                series={[
                                    {
                                        dataKey: 'value',
                                        id: 'sync',
                                    }
                                ]}
                                colors={colorScheme}
                                xAxis={[{ dataKey: 'label', scaleType: 'band' }]}
                                height={400}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant='h3' marginTop={5} marginBottom={5}>
                                <DirectionsBoatFilledOutlinedIcon /> Total numbers of Ships to {selectedDestination}
                            </Typography>
                            <BarChart
                                dataset={lineChartMarketShipSumFiltered}
                                series={[
                                    {
                                        dataKey: 'value',
                                        id: 'sync',
                                    }
                                ]}
                                colors={colorScheme}
                                xAxis={[{ dataKey: 'label', scaleType: 'band' }]}
                                height={400}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant='h3' marginTop={5} marginBottom={5}>
                                Total numbers of Cars and Ships per Month to {selectedDestination}
                            </Typography>
                            <Box>
                                <StandardDataGrid rows={tableDataMarketSumFiltered.rows} columns={tableDataMarketSumFiltered.columns} columnIdVisibiliy={true} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant='h3' marginTop={5} marginBottom={5}>
                                <AnchorOutlinedIcon /> Share per Port of car volumes shipped to {selectedDestination} of the {timeRange}
                            </Typography>
                            <PieChartBasic
                                data={pieChartData}
                                height={400}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
            {selectedPort && (
                <>
                    <Box sx={{ height: "60vh", width: "100%" }}>
                        <StandardDataGrid
                            rows={filteredData}
                            columns={columns}
                            columnIdVisibiliy={false}
                            additionalInitialState={{
                                sorting: {
                                    sortModel: [{ field: 'latestETD', sort: 'desc' }],
                                },
                            }}
                        />
                    </Box>
                    <Box sx={{ height: 400, mt: 4 }}>
                        <Typography variant="h3" marginTop={5}>
                            {`Cars shipped per Month`}
                        </Typography>
                        <LineChartBasic data={chartData} height={400} />
                    </Box>
                </>
            )}
        </Box>
    );
};


export { ShippingReportPage };
